import { ESdChangeReason, getDictionaryValueByKey, IWorkFlow } from "@kuika/kuika-cl-model";
import { IHappyPathPhaseModel, IWFStep } from "@kuika/kuika-cl-model/src/models/workflow";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import "./workflow-phase-properties.scss";

const PHASE_KEY: string = "happyPath";

export interface IWorkFlowPhasePropertiesProps {
  phaseId: string;
}

export interface IWorkFlowPhasePropertiesState {
  nameDraft: string;
}

/**
 * Right-panel properties for a selected Happy Path phase (a `bpmn:Group`, visual-only — it has no
 * JSON node). The ONLY editable field is the phase name (renaming drives the canvas group's label
 * through `sd.renameWfPhase`, so canvas + model never diverge). Everything else is READ-ONLY
 * information: the steps this phase covers and where it sits in the overall phase sequence. Order
 * and step membership are geometry-driven on the canvas (drag columns / steps) and are deliberately
 * not editable here.
 */
class WorkFlowPhaseProperties extends PureSdComponent<IWorkFlowPhasePropertiesProps, IWorkFlowPhasePropertiesState> {
  /** True while the name input is focused — suppresses adopting external renames over an unsaved edit. */
  private isEditing: boolean = false;

  constructor(props: IWorkFlowPhasePropertiesProps & IWithSDProps) {
    super(props);
    this.state = { nameDraft: phaseNameOf(props.sd.selectedWf, props.phaseId) };
  }

  componentDidUpdate(prevProps: IWorkFlowPhasePropertiesProps): void {
    if (prevProps.phaseId !== this.props.phaseId) {
      this.setState({ nameDraft: this.getPhaseName() });
      return;
    }
    // External rename (canvas double-click / collab) while this panel is open — adopt it unless the
    // user has an unsaved edit in progress.
    const persisted: string = this.getPhaseName();
    if (!this.isEditing && persisted !== this.state.nameDraft) {
      this.setState({ nameDraft: persisted });
    }
  }

  private getPhase = (): IHappyPathPhaseModel | undefined => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const phases: IHappyPathPhaseModel[] = Array.isArray(wf?.happyPathDefinition) ? wf!.happyPathDefinition : [];
    return phases.find((p: IHappyPathPhaseModel): boolean => p.id === this.props.phaseId);
  };

  private getPhaseName = (): string => {
    return this.getPhase()?.happyPathPhase ?? "";
  };

  private stepsByPhase = (): Map<string, IWFStep[]> => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const map: Map<string, IWFStep[]> = new Map<string, IWFStep[]>();
    if (wf?.nodes?.steps) {
      this.props.sd.iterateAllStepsRecursively(wf.nodes.steps, (step: IWFStep): void => {
        const pid: string = getDictionaryValueByKey(step.extraParameters, PHASE_KEY);
        if (!pid) return;
        const bucket: IWFStep[] = map.get(pid) ?? [];
        bucket.push(step);
        map.set(pid, bucket);
      });
    }
    return map;
  };

  private commitName = (): void => {
    this.isEditing = false;
    const trimmed: string = this.state.nameDraft.trim();
    const current: string = this.getPhaseName();
    if (!trimmed || trimmed === current) {
      // Empty or unchanged — revert the input to the persisted name.
      if (this.state.nameDraft !== current) this.setState({ nameDraft: current });
      return;
    }
    this.props.sd.renameWfPhase(this.props.phaseId, trimmed);
  };

  render(): JSX.Element {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const phase: IHappyPathPhaseModel | undefined = this.getPhase();
    if (!wf || !phase) return <></>;

    const readOnly: boolean = this.props.sd.selectedWfIsReadOnly === true;
    const byPhase: Map<string, IWFStep[]> = this.stepsByPhase();
    const steps: IWFStep[] = byPhase.get(phase.id) ?? [];
    const phases: IHappyPathPhaseModel[] = [...(wf.happyPathDefinition ?? [])].sort(
      (a: IHappyPathPhaseModel, b: IHappyPathPhaseModel): number => a.phaseOrder - b.phaseOrder
    );

    return (
      <div className="kuika-wf-phase">
        <section className="kuika-wf-phase__section">
          <div className="kuika-wf-phase__section-head">
            <span className="kuika-wf-phase__section-title">Phase Name</span>
          </div>
          <KuikaInput
            data-testid="phaseNameInput"
            value={this.state.nameDraft}
            readOnly={readOnly}
            disabled={readOnly}
            placeholder="Phase name"
            onFocus={() => {
              this.isEditing = true;
            }}
            onChange={(_e, value) => this.setState({ nameDraft: String(value) })}
            onBlur={() => this.commitName()}
            onPressEnter={() => this.commitName()}
          />
        </section>

        <section className="kuika-wf-phase__section">
          <div className="kuika-wf-phase__section-head">
            <span className="kuika-wf-phase__section-title">Steps in this phase</span>
            <span className="kuika-wf-phase__count-badge">{steps.length}</span>
          </div>
          {steps.length === 0 ? (
            <span className="kuika-wf-phase__empty">
              No steps yet — drag a step into this phase column on the canvas.
            </span>
          ) : (
            <ul className="kuika-wf-phase__step-list">
              {steps.map((step: IWFStep) => (
                <li className="kuika-wf-phase__step-item" key={step.id} title={step.name}>
                  {step.name || "Untitled step"}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="kuika-wf-phase__section">
          <div className="kuika-wf-phase__section-head">
            <span className="kuika-wf-phase__section-title">Process phases</span>
          </div>
          <ol className="kuika-wf-phase__phase-list">
            {phases.map((p: IHappyPathPhaseModel) => {
              const isCurrent: boolean = p.id === phase.id;
              const count: number = (byPhase.get(p.id) ?? []).length;
              const itemClass: string = isCurrent
                ? "kuika-wf-phase__phase-item kuika-wf-phase__phase-item--current"
                : "kuika-wf-phase__phase-item";
              return (
                <li className={itemClass} key={p.id} title={p.happyPathPhase}>
                  <span className="kuika-wf-phase__phase-order">{p.phaseOrder}</span>
                  <span className="kuika-wf-phase__phase-name">{p.happyPathPhase}</span>
                  <span className="kuika-wf-phase__phase-count">{count}</span>
                </li>
              );
            })}
          </ol>
        </section>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.SaveWorkflow ||
      changeReason === ESdChangeReason.ClickWfDesignXmlNode ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

/** Persisted display name of a phase by id — used for the initial input value at construction. */
function phaseNameOf(wf: IWorkFlow | undefined, phaseId: string): string {
  const phases: IHappyPathPhaseModel[] = Array.isArray(wf?.happyPathDefinition) ? wf!.happyPathDefinition : [];
  return phases.find((p: IHappyPathPhaseModel): boolean => p.id === phaseId)?.happyPathPhase ?? "";
}

const workFlowPhaseProperties = withContext<IWorkFlowPhasePropertiesProps>(
  withSD<IWorkFlowPhasePropertiesProps>(WorkFlowPhaseProperties)
);
export { workFlowPhaseProperties as WorkFlowPhaseProperties };
